import { computedFrom, autoinject } from 'aurelia-framework';
import { Customer, Payment, Account } from '../models/customer';
import { Features } from '../models/features';
import { I18N } from 'aurelia-i18n';
import { BasePage } from '../pages/base-page';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Portal } from '../models/portal';

@autoinject
export class State extends BasePage {
	features: Features;
	question1List: any;
	question2List: any;
	passwordRulesHtml: any;
	_customer: Customer;
	_associatedCustomers: Array<Customer>;
	_accounts: Array<Account>;
	business: any;
	_payments: any;
	account: Account;
	isLoggingIn: boolean;
    documentLists: any;
    hideEzpayTerms: boolean;
    hideSignupTerms: boolean;

	constructor(private i18n: I18N, eventAggregator: EventAggregator) {
		super(eventAggregator);
		this.features = new Features(null);
		this._customer = null;
        this.business = null;
        this.hideEzpayTerms = false;
        this.hideSignupTerms = false;
	}
	attached() {
		super.attached();
	}
	loadTranslations() {
		this.passwordRulesHtml =
			this.i18n.i18next.t(
				'vtChangePasswordDescriptionFormatted',
				'<p>This system requires strong passwords.  Your new password must be:</p><ul><li>a minimum of 12 characters</li><li>include at least three of the following four categories:<ul><li>lower case letter (a-z)</li><li>one upper case letter (A-Z)</li><li>numeric digit (0-9)</li><li>special character (@$!%*?)</li></ul></li><li>Additionally, password must not share 3 or more consecutive characters from the username, first, or last name</li></ul>');

		this.question1List = [
			this.i18n.i18next.t('nameOfFirstAndLastNameOfFirstBoyfriendOrGirlfriend', 'What is the first and last name of your first boyfriend or girlfriend?'),
			this.i18n.i18next.t('phoneNumberFromChildhood', 'Which phone number do you remember most from your childhood?'),
			this.i18n.i18next.t('favoritePlaceToVisitAsChild', 'What was your favorite place to visit as a child?'),
			this.i18n.i18next.t('favoriteActorMusicianOrArtist', 'Who is your favorite actor, musician, or artist?'),
			this.i18n.i18next.t('nameOfFavoritePet', 'What is the name of your favorite pet?'),
			this.i18n.i18next.t('cityOfBirth', 'In what city were you born?'),
			this.i18n.i18next.t('highSchoolAttended', 'What high school did you attend?'),
			this.i18n.i18next.t('nameOfFirstSchool', 'What is the name of your first school?'),
			this.i18n.i18next.t('favoriteMovie', 'What is your favorite movie?'),
			this.i18n.i18next.t('mothersMaidenName', 'What is your mother\'s maiden name?'),
			this.i18n.i18next.t('streetOfGrowth', 'What street did you grow up on?'),
			this.i18n.i18next.t('makeOfFirstCar', 'What was the make of your first car?'),
			this.i18n.i18next.t('whenIsAnniversary', 'When is your anniversary?'),
			this.i18n.i18next.t('favoriteColor', 'What is your favorite color?'),
			this.i18n.i18next.t('fathersMiddleName', 'What is your father\'s middle name?'),
			this.i18n.i18next.t('nameOfFirstGradeTeacher', 'What is the name of your first grade teacher?'),
			this.i18n.i18next.t('highSchoolMascot', 'What was your high school mascot?'),
			this.i18n.i18next.t('favoriteWebBrowser', 'Which is your favorite web browser?')
	];

	// Check if these are used, if not delete.
	this.question2List = [
		'Why are you wearing that?',
		'When do you think the world will end?',
		'Do these pants make me look fat?',
		'What do the voices in your head say in the early morning?',
		'Why purple?'
	];
}
	logout() {
		this.customer = null;
		this.payments = null;
		this.token = this.anonymousToken;
	}

	@computedFrom('_customer')
	get customer(): Customer {
		if (!this._customer) {
			this._customer = new Customer(this.business);
		}

		return this._customer;
	}

	set customer(value: Customer) {
		this._customer = value;
		sessionStorage.customer = JSON.stringify(value);

		if (this._customer && this._payments) {
			this._customer.calculateLastActivity(this._payments, this.i18n);
		}
	}

	@computedFrom('_associatedCustomers')
	get associatedCustomers(): Array<Customer> {
		if (!this._associatedCustomers) {
			this._associatedCustomers = [];
		}

		return this._associatedCustomers;
	}

	set associatedCustomers(value: Array<Customer>) {
		this._associatedCustomers = value;
		sessionStorage.customers = JSON.stringify(value);

		// TODO: Calculate last activity?
	}

	@computedFrom('_accounts')
	get accounts(): Array<Account> {
		let accounts = this.customer.accounts || [];
		this.associatedCustomers.forEach(x => {
			accounts = accounts.concat(x.accounts);
		});

		return accounts;
	}

	@computedFrom('_payments')
	get payments() {
		if (!this._payments) {
			this._payments = Payment.fetchFromSession();
		}
		return this._payments;
	}
	set payments(value) {
		this._payments = value;
		sessionStorage.payments = JSON.stringify(value);

		if (this._customer && this._payments) {
			this._customer.calculateLastActivity(this._payments, this.i18n);
		}
	}

	@computedFrom('_customer')
	get hasPayByTextEnabledPortfolio() {
		try {
			for (let n in this._customer.portfolios) {
				if (this._customer.portfolios[n].portalSettings) {
					let settings = JSON.parse(this._customer.portfolios[n].portalSettings);

					if ('enablePayByText' in settings && settings.enablePayByText) {
						return true;
					}
				}
			}

			return false;
		}
		catch (err) {
			console.error('Error encountered...');
			return false;
		}
	}

	@computedFrom('_customer')
	get hasCreditCardEnabledPortfolio() {
		try {
			for (let n in this._customer.portfolios) {
				if (this._customer.portfolios[n].portalSettings) {
                    let settings = JSON.parse(this._customer.portfolios[n].portalSettings);

					if ('paymentMethods' in settings && settings.paymentMethods && 'allowCreditCard' in settings.paymentMethods && settings.paymentMethods.allowCreditCard) {
						return true;
					}
				}
			}

			return false;
		}
		catch (err) {
			console.error('Error encountered...');
			return false;
		}
	}

	@computedFrom('_customer')
	get hasECheckEnabledPortfolio() {
		try {
			for (let n in this._customer.portfolios) {
				if (this._customer.portfolios[n].portalSettings) {
					let settings = JSON.parse(this._customer.portfolios[n].portalSettings)

					if (
						'paymentMethods' in settings
						&& settings.paymentMethods
						&& 'allowAch' in settings.paymentMethods
						&& settings.paymentMethods.allowAch
					) {
						return true;
					}
				}
			}

			return false;
		}
		catch (err) {
			console.error('Error encountered...');
			return false;
		}
	}

	@computedFrom('business')
	get isConsumerPortalGuarantorBillingEnabled(): boolean {
		try {
			let portal: Portal = this.business.portalSettings;
			return portal.settings.enableAddCustomerToPortalUser;
		} catch (err) {
			return false;
		}
	}

	get token() {
		return sessionStorage.getItem('token') || '';
	}

	set token(value: string) {
		sessionStorage.setItem('token', value);
	}

	get anonymousToken() {
		return sessionStorage.getItem('anonymousToken') || '';
	}

	set anonymousToken(value: string) {
		sessionStorage.setItem('anonymousToken', value);
	}

	updateAccountStateOffline(id: any, state: any) {
		this.account = this.customer.accounts.find((element, index, array) => {
			return element.id === id;
		});
		this.account.state = state;
	}

}
